import * as React from 'react'
import axios from 'axios'
import routes from '../../routes'

// Cancelling stops the renewal; it never takes away days that were paid for.
// The cheaper plan comes before the losses, and keeping the plan is the
// primary action, because most people who open this are reacting to a price
// rather than to the product.
const LOSSES = [
    'Unlimited graphs and AI chats',
    'Large file and PDF imports',
    'Live updates and scheduled imports',
    'API, MCP and integration access at your plan’s limits',
]

// The backend's churn reason codes (profile.constants CHURN_REASONS), in the
// order they are shown. One optional line of free text goes with the pick.
export const CANCEL_REASONS = [
    ['too_expensive', 'Too expensive for what I use'],
    ['no_time', 'No time to use it right now'],
    ['did_not_understand', 'Could not work out how to use it'],
    ['no_insights', 'Did not get useful insights from my graphs'],
    ['missing_feature', 'A feature I need is missing'],
    ['switched_tool', 'Switched to another tool'],
    ['project_ended', 'My project ended'],
    ['bugs', 'Bugs or performance problems'],
    ['language', 'My language is not supported well enough'],
    ['other', 'Something else'],
]

// Fire-and-forget to the profile: the cancellation must never wait on, or
// fail because of, the reason being recorded.
function recordReason({ reason, text }) {
    if (!reason && !text) return Promise.resolve()
    return axios
        .post('/me/profile/churn', {
            ...(reason ? { reason } : {}),
            ...(text ? { text: text.slice(0, 2000) } : {}),
        })
        .catch(() => null)
}

export default function CancelSubscriptionDialog({
    open,
    planLabel,
    endsOn,
    cheaperPlanLabel,
    onClose,
    onCancelled,
}) {
    const [step, setStep] = React.useState('confirm') // confirm | reason
    const [reason, setReason] = React.useState(null)
    const [text, setText] = React.useState('')
    const [status, setStatus] = React.useState('idle')
    const [error, setError] = React.useState(null)
    const panelRef = React.useRef(null)

    React.useEffect(() => {
        if (!open) {
            setStep('confirm')
            setReason(null)
            setText('')
            setStatus('idle')
            setError(null)
            return undefined
        }
        const onKeyDown = event => {
            if (event.key === 'Escape' && status !== 'working') onClose()
        }
        document.addEventListener('keydown', onKeyDown)
        panelRef.current?.querySelector('button')?.focus()
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open, onClose, status])

    if (!open) return null

    async function cancelPlan() {
        setStatus('working')
        setError(null)
        try {
            recordReason({ reason, text: text.trim() })
            const response = await axios.post('/billing/cancel', {})
            onCancelled(response.data)
        } catch (requestError) {
            setStatus('failed')
            setError(
                requestError?.response?.data?.message ||
                    'The cancellation did not go through. Please try again.'
            )
        }
    }

    const keepButton = (
        <button
            type="button"
            onClick={onClose}
            disabled={status === 'working'}
            data-testid="cancel-keep"
            className="tw-cursor-pointer tw-rounded-lg tw-border-0 tw-bg-primary tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-primary-foreground disabled:tw-opacity-50"
        >
            Keep your {planLabel || 'plan'}
        </button>
    )

    return (
        <div className="project-notes-scope">
            <div
                className="tw-fixed tw-inset-0 tw-z-[1400] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-p-4"
                onMouseDown={event => {
                    if (
                        event.target === event.currentTarget &&
                        status !== 'working'
                    )
                        onClose()
                }}
            >
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cancel-dialog-title"
                    data-testid="cancel-dialog"
                    data-step={step}
                    className="tw-w-full tw-max-w-md tw-rounded-xl tw-border tw-border-solid tw-border-border tw-bg-background tw-p-6 tw-text-foreground tw-shadow-xl"
                >
                    {step === 'confirm' ? (
                        <>
                            <h2
                                id="cancel-dialog-title"
                                className="tw-m-0 tw-text-lg tw-font-semibold"
                            >
                                Cancel plan
                            </h2>
                            <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-muted-foreground">
                                Cancel to stop recurring billing. You can still
                                use {planLabel || 'your plan'}
                                {endsOn
                                    ? ` until ${endsOn}`
                                    : ' until the end of this billing period'}
                                .
                            </p>

                            {cheaperPlanLabel ? (
                                <p className="tw-m-0 tw-mt-3 tw-text-sm">
                                    Paying for more than you need?{' '}
                                    <a
                                        href={`${routes.upgrade}?return=${routes.subscription}`}
                                        className="tw-text-primary"
                                        data-testid="cancel-switch-instead"
                                    >
                                        Switch to {cheaperPlanLabel} instead
                                    </a>
                                    .
                                </p>
                            ) : null}

                            <p className="tw-m-0 tw-mt-4 tw-text-sm tw-font-medium">
                                You’ll lose access to
                            </p>
                            <ul className="tw-m-0 tw-mt-1 tw-list-disc tw-pl-5 tw-text-sm tw-text-muted-foreground">
                                {LOSSES.map(line => (
                                    <li key={line}>{line}</li>
                                ))}
                            </ul>

                            <div className="tw-mt-5 tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3">
                                <button
                                    type="button"
                                    onClick={() => setStep('reason')}
                                    data-testid="cancel-continue"
                                    className="tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-destructive tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-text-destructive"
                                >
                                    Cancel plan
                                </button>
                                {keepButton}
                            </div>
                        </>
                    ) : (
                        <>
                            <h2
                                id="cancel-dialog-title"
                                className="tw-m-0 tw-text-lg tw-font-semibold"
                            >
                                Before you go
                            </h2>
                            <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-muted-foreground">
                                What is the main reason? One answer helps us
                                more than you would think. Skipping is fine.
                            </p>
                            <fieldset
                                className="tw-m-0 tw-mt-3 tw-border-0 tw-p-0"
                                data-testid="cancel-reasons"
                            >
                                <legend className="tw-sr-only">Reason</legend>
                                <ul className="tw-m-0 tw-grid tw-list-none tw-gap-1.5 tw-p-0">
                                    {CANCEL_REASONS.map(([code, label]) => (
                                        <li key={code}>
                                            <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
                                                <input
                                                    type="radio"
                                                    name="cancel-reason"
                                                    value={code}
                                                    checked={reason === code}
                                                    onChange={() =>
                                                        setReason(code)
                                                    }
                                                    data-testid={`cancel-reason-${code}`}
                                                    className="tw-cursor-pointer"
                                                />
                                                {label}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            </fieldset>
                            <textarea
                                value={text}
                                onChange={event => setText(event.target.value)}
                                maxLength={2000}
                                rows={2}
                                placeholder="Anything you want to add (optional)"
                                aria-label="Anything you want to add"
                                data-testid="cancel-reason-text"
                                className="tw-mt-3 tw-w-full tw-resize-y tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-background tw-p-2 tw-text-sm tw-text-foreground"
                            />

                            {error ? (
                                <p
                                    className="tw-m-0 tw-mt-3 tw-text-sm tw-text-destructive"
                                    role="alert"
                                    data-testid="cancel-error"
                                >
                                    {error}
                                </p>
                            ) : null}

                            <div className="tw-mt-5 tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3">
                                <button
                                    type="button"
                                    onClick={cancelPlan}
                                    disabled={status === 'working'}
                                    data-testid="cancel-confirm"
                                    className="tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-destructive tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-text-destructive disabled:tw-opacity-50"
                                >
                                    {status === 'working'
                                        ? 'Cancelling…'
                                        : reason || text.trim()
                                        ? 'Send and cancel plan'
                                        : 'Skip and cancel plan'}
                                </button>
                                {keepButton}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
